import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, ShieldCheck, RotateCcw, Phone, Navigation } from "lucide-react";
import { Logo } from "@/components/Logo";
import { openCookiePreferences, useCookieBannerEnabled } from "@/lib/consent";
import { trackEvent } from "@/lib/track";
import api from "@/lib/api";

const mapsUrl = (store) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${store.address}, ${store.postal_code || ""} ${store.city}, ${store.country || ""}`
  )}`;

const StoreCard = ({ store }) => (
  <div data-testid={`footer-store-${store.id}`} className="text-sm text-cream/70 leading-relaxed">
    <p className="text-cream/90 font-medium">{store.name}</p>
    <p className="mt-1">{store.address}, {store.postal_code} {store.city}</p>
    <div className="flex items-center gap-4 mt-2">
      <a
        href={`tel:${store.phone.replace(/\s+/g, "")}`}
        onClick={() => trackEvent("store_call_click", { store_id: store.id, store_name: store.name })}
        className="flex items-center gap-1.5 text-gold hover:text-cream transition-colors"
      >
        <Phone className="h-3.5 w-3.5" /> Appeler
      </a>
      <a
        href={mapsUrl(store)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackEvent("store_directions_click", { store_id: store.id, store_name: store.name })}
        className="flex items-center gap-1.5 text-gold hover:text-cream transition-colors"
      >
        <Navigation className="h-3.5 w-3.5" /> Itinéraire
      </a>
    </div>
  </div>
);

export const Footer = () => {
  const bannerEnabled = useCookieBannerEnabled();
  const [stores, setStores] = useState([]);

  useEffect(() => {
    api.get("/stores").then(({ data }) => setStores(data)).catch(() => {});
  }, []);

  return (
  <footer data-testid="site-footer" className="bg-ink text-cream mt-24 md:mt-32">
    <div className="grid grid-cols-2 md:grid-cols-3 gap-8 px-6 md:px-12 py-10 border-b border-white/10">
      <div className="flex items-start gap-3">
        <Truck className="h-6 w-6 text-gold shrink-0" strokeWidth={1.4} />
        <div>
          <p className="font-sans text-sm font-semibold">Livraison gratuite</p>
          <p className="text-xs text-cream/60 mt-1">Dès 50€ d'achat, partout en Europe.</p>
        </div>
      </div>
      <div className="flex items-start gap-3">
        <RotateCcw className="h-6 w-6 text-gold shrink-0" strokeWidth={1.4} />
        <div>
          <p className="font-sans text-sm font-semibold">Retour 15 jours</p>
          <p className="text-xs text-cream/60 mt-1">Remboursement immédiat si non conforme.</p>
        </div>
      </div>
      <div className="flex items-start gap-3">
        <ShieldCheck className="h-6 w-6 text-gold shrink-0" strokeWidth={1.4} />
        <div>
          <p className="font-sans text-sm font-semibold">Garantie 1 an</p>
          <p className="text-xs text-cream/60 mt-1">Couvre les défauts de fabrication.</p>
        </div>
      </div>
    </div>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-10 px-6 md:px-12 py-14">
      <div className="col-span-2 md:col-span-1">
        <Logo to={null} size="lg" variant="light" showTagline testId="footer-logo" />
        <p className="text-sm text-cream/60 mt-5 max-w-xs leading-relaxed">
          Les meilleures marques de luminaires au meilleur prix. Jusqu'à 70% de réduction.
        </p>
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-cream/40 mb-4">Catégories</p>
        <ul className="space-y-3 text-sm text-cream/80">
          <li><Link to="/categorie/suspensions" className="hover:text-gold transition-colors">Suspensions</Link></li>
          <li><Link to="/categorie/plafonniers" className="hover:text-gold transition-colors">Plafonniers</Link></li>
          <li><Link to="/categorie/lampadaires" className="hover:text-gold transition-colors">Lampadaires</Link></li>
          <li><Link to="/categorie/appliques" className="hover:text-gold transition-colors">Appliques</Link></li>
        </ul>
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-cream/40 mb-4">Compte</p>
        <ul className="space-y-3 text-sm text-cream/80">
          <li><Link to="/connexion" className="hover:text-gold transition-colors">Se connecter</Link></li>
          <li><Link to="/compte" className="hover:text-gold transition-colors">Mes commandes</Link></li>
          <li><Link to="/boutique" className="hover:text-gold transition-colors">Toute la boutique</Link></li>
        </ul>
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-cream/40 mb-4">Nos magasins</p>
        <div className="space-y-4">
          {stores.map((s) => <StoreCard key={s.id} store={s} />)}
        </div>
      </div>
    </div>
    <div className="px-6 md:px-12 py-6 border-t border-white/10 flex flex-col sm:flex-row gap-3 sm:gap-6 sm:items-center text-xs text-cream/40">
      <p>© {new Date().getFullYear()} Déstockage Luminaires. Tous droits réservés.</p>
      <Link to="/confidentialite" className="hover:text-gold transition-colors">
        Politique de confidentialité
      </Link>
      {bannerEnabled && (
        <button onClick={openCookiePreferences} className="hover:text-gold transition-colors text-left">
          Gérer les cookies
        </button>
      )}
    </div>
  </footer>
  );
};
